import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiFolder, FiArrowLeft, FiShield, FiCheck, FiLock } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { createDataRoom, check2FAStatus } from '../services/dataRoomService';
import TwoFactorSetup from '../components/TwoFactorSetup';

const CreateDataRoom: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAdmin, loading: adminLoading } = useSubscription();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [has2FA, setHas2FA] = useState<boolean | null>(null);
  const [show2FASetup, setShow2FASetup] = useState(false);

  useEffect(() => {
    checkTwoFactorStatus();
  }, []);

  const checkTwoFactorStatus = async () => {
    const enabled = await check2FAStatus();
    setHas2FA(enabled);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    if (!has2FA) {
      setError('You must enable 2FA before creating a data room');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const room = await createDataRoom(name.trim(), description.trim() || undefined);
      navigate(`/data-rooms/${room.id}`);
    } catch (err: any) {
      console.error('Error creating data room:', err);
      setError(err.message || 'Failed to create data room');
    } finally {
      setLoading(false);
    }
  };

  const handle2FASuccess = () => {
    setShow2FASetup(false);
    setHas2FA(true);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <p className="text-gray-400">Please log in to create a Data Room</p>
      </div>
    );
  }

  // Show loading while checking admin status or 2FA status
  if (adminLoading || has2FA === null) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Only admins can create data rooms
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-950 pt-20 pb-12 px-4">
        <div className="max-w-lg mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-900/50 rounded-2xl border border-red-500/30 p-8 text-center"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-red-600/20 to-orange-600/20 border border-red-500/30 flex items-center justify-center">
              <FiLock className="w-10 h-10 text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">
              Admin Access Required
            </h2>
            <p className="text-gray-400 mb-6">
              Only administrators can create new data rooms. Contact your admin if you need a new data room created.
            </p>
            <button
              onClick={() => navigate('/data-rooms')}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-semibold hover:from-purple-500 hover:to-cyan-500 transition-all"
            >
              Back to Data Room
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 pt-20 pb-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate('/data-rooms')}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-4 transition-colors"
          >
            <FiArrowLeft className="w-4 h-4" />
            Back to Data Room
          </button>

          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <FiFolder className="text-cyan-400" />
            Create New Data Room
          </h1>
          <p className="text-gray-400 mt-2">
            Create a secure space for sharing documents with institutional investors
          </p>
        </motion.div>

        {/* 2FA Required Notice */}
        {!has2FA && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-6 rounded-2xl bg-amber-500/10 border border-amber-500/30"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                <FiShield className="w-6 h-6 text-amber-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-amber-400 mb-2">
                  Two-Factor Authentication Required
                </h3>
                <p className="text-amber-400/70 text-sm mb-4">
                  For security purposes, you must enable Two-Factor Authentication (Google Authenticator) 
                  before you can create or manage data rooms. This protects sensitive investor documents.
                </p>
                <button
                  onClick={() => setShow2FASetup(true)}
                  className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-600 to-orange-600 text-white font-medium hover:from-amber-500 hover:to-orange-500 transition-all"
                >
                  Enable 2FA Now
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* 2FA Enabled Badge */}
        {has2FA && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 rounded-xl bg-green-500/10 border border-green-500/30 flex items-center gap-3"
          >
            <FiCheck className="w-5 h-5 text-green-400" />
            <span className="text-green-400 text-sm">Two-Factor Authentication is enabled</span>
          </motion.div>
        )}

        {/* Create Form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onSubmit={handleSubmit}
          className="p-6 rounded-2xl bg-gray-900/50 border border-gray-800/50"
        >
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Data Room Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g., Series A Due Diligence"
                className="w-full p-4 rounded-xl bg-gray-800/50 border border-gray-700/50 text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none transition-all"
                required
                disabled={!has2FA}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Brief description of this data room's purpose..."
                rows={4}
                className="w-full p-4 rounded-xl bg-gray-800/50 border border-gray-700/50 text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none transition-all resize-none"
                disabled={!has2FA}
              />
            </div>

            {error && (
              <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {error}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => navigate('/data-rooms')}
                className="px-5 py-3 rounded-xl text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !has2FA || !name.trim()}
                className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-medium hover:from-purple-500 hover:to-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-purple-500/25 flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Data Room'
                )}
              </button>
            </div>
          </div>
        </motion.form>
      </div>

      {/* 2FA Setup Modal */}
      {show2FASetup && user && (
        <TwoFactorSetup
          isOpen={show2FASetup}
          onClose={() => setShow2FASetup(false)}
          userEmail={user.email || ''}
          onSuccess={handle2FASuccess}
        />
      )}
    </div>
  );
};

export default CreateDataRoom;
